import { useEffect, useRef, useState, useContext } from 'react';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Select from 'react-select';
import { useNavigate } from 'react-router-dom';
import useAuth from '../../../auth/useAuth';
import usePermission from '../../../hooks/usePermission';
import { useLocationsDropdown } from '../../../APIs/common';
import { ConfigContext } from '../../../contexts/ConfigContext';
import { reactSelectDark } from '../../../styles/reactSelectDarkTheme';
import './FloatingActionMenu.css';

const FAB_SIZE = 58;
const FAB_EDGE = 12;
const DRAG_SLOP = 5;
const FAB_POS_KEY = 'yz-fab-pos';

/** The bottom of the top bar: the button (and its actions) stay below it, as
 *  the tablet's POS button stays inside the screen's body. */
const headerBottom = () => {
  const h = document.querySelector('.pc-header');
  return h ? Math.max(0, h.getBoundingClientRect().bottom) : 0;
};

/** Keeps the button wholly on screen under the top bar, a little in from the edges. */
const clampPos = ({ x, y }) => {
  const top = headerBottom() + FAB_EDGE;
  return {
    x: Math.min(Math.max(x, FAB_EDGE), Math.max(FAB_EDGE, window.innerWidth - FAB_SIZE - FAB_EDGE)),
    y: Math.min(Math.max(y, top), Math.max(top, window.innerHeight - FAB_SIZE - FAB_EDGE))
  };
};

/** Where the button was last dropped in this browser, or null for its corner. */
function readSavedPos() {
  try {
    const p = JSON.parse(localStorage.getItem(FAB_POS_KEY) || 'null');
    return p && Number.isFinite(p.x) && Number.isFinite(p.y) ? clampPos(p) : null;
  } catch {
    return null;
  }
}

export default function FloatingActionMenu() {
  const [open, setOpen] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const config = useContext(ConfigContext);
  const [location, setLocation] = useState(null);
  const wrapperRef = useRef(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { has } = usePermission();
  // Redeem opens the one scanner for gift cards, vouchers and tickets, so it
  // shows to a role that may redeem any of them.
  const canRedeem = has('giftcard_redeem') || has('voucher_scan') || has('ticket_scan');

  const { locations, isLoading: isLocationLoading } = useLocationsDropdown();
  const go = (path) => {
    setOpen(false);
    navigate(path);
  };

  const locationOptions =
    locations?.map((loc) => ({
      value: loc.id,
      label: loc.name
    })) || [];

  useEffect(() => {
    function handleClickOutside(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    }

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  // ── Dragging, as the tablet's POS button ──
  // The + can be picked up and dropped anywhere on the screen; a press that
  // moves less than a few pixels is still a click. Where it was left is kept
  // in this browser. Until it is first moved it sits in its corner.
  const [pos, setPos] = useState(readSavedPos);
  const [dragging, setDragging] = useState(false);
  const drag = useRef(null);
  const justDragged = useRef(false);

  const onPointerDown = (e) => {
    if (e.button !== 0) return;
    const rect = wrapperRef.current.getBoundingClientRect();
    drag.current = { startX: e.clientX, startY: e.clientY, dx: e.clientX - rect.left, dy: e.clientY - rect.top, moved: false };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const onPointerMove = (e) => {
    const d = drag.current;
    if (!d) return;
    if (!d.moved) {
      if (Math.hypot(e.clientX - d.startX, e.clientY - d.startY) < DRAG_SLOP) return;
      d.moved = true;
      setDragging(true);
      setOpen(false);
    }
    setPos(clampPos({ x: e.clientX - d.dx, y: e.clientY - d.dy }));
  };

  const onPointerUp = () => {
    const d = drag.current;
    drag.current = null;
    if (!d?.moved) return;
    justDragged.current = true;
    setDragging(false);
    setPos((p) => {
      try {
        if (p) localStorage.setItem(FAB_POS_KEY, JSON.stringify(p));
      } catch {
        /* storage blocked — it just won't be remembered */
      }
      return p;
    });
  };

  // Kept on screen when the window shrinks.
  useEffect(() => {
    const onResize = () => setPos((p) => (p ? clampPos(p) : p));
    onResize(); // once on mount too, now the top bar is on the page
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // The arc opens away from the nearest edges: up or down, left or right.
  const centre = pos ? { x: pos.x + FAB_SIZE / 2, y: pos.y + FAB_SIZE / 2 } : null;
  const fanRight = centre ? centre.x < window.innerWidth / 2 : false;
  // Up or down by where there is more room: the space above is measured from
  // the top bar, not the top of the window.
  const fanDown = centre ? centre.y - headerBottom() < window.innerHeight - centre.y : false;

  // const proceedToScan = () => {
  //   if (!location) return;

  //   setShowLocationModal(false);
  //   setOpen(false);

  //   navigate('/gift_card/scan', {
  //     state: { locationId: location }
  //   });
  // };

  const proceedToScan = () => {
    if (!location) return;

    sessionStorage.setItem('selectedLocationId', location.value);

    setShowLocationModal(false);
    setOpen(false);

    navigate('/gift_card/scan');
  };

  useEffect(() => {
    if (!showLocationModal) {
      setLocation(null);
    }
  }, [showLocationModal]);

  // The quick actions, fanned out round the + on a quarter circle (straight
  // up to straight left). Each is a round icon; its name shows as a hint on
  // hover or keyboard focus.
  const actions = [
    { key: 'booking', label: 'Add Booking', icon: 'ph-calendar-plus', onClick: () => go('/booking-management/new-booking') },
    { key: 'gift', label: 'Add Gift Card', icon: 'ph-gift', onClick: () => go('/gift_card/add') },
    canRedeem && {
      key: 'redeem',
      label: 'Redeem',
      icon: 'ph-scan',
      onClick: () => {
        if (user?.role?.name === 'merchant_admin') {
          setShowLocationModal(true);
        } else {
          go('/gift_card/scan');
        }
      }
    }
  ].filter(Boolean);

  const RADIUS = 92;
  const place = (i, n) => {
    const deg = n === 1 ? 135 : 90 + (90 * i) / (n - 1);
    const rad = (deg * Math.PI) / 180;
    const x = Math.abs(Math.cos(rad)) * RADIUS * (fanRight ? 1 : -1);
    const y = Math.abs(Math.sin(rad)) * RADIUS * (fanDown ? 1 : -1);
    return {
      '--fab-x': `${Math.round(x)}px`,
      '--fab-y': `${Math.round(y)}px`,
      '--fab-delay': `${i * 40}ms`
    };
  };
  // The icon straight above (or below) the + shows its hint above (or below)
  // itself, away from the arc — at its side the hint ran over the next icon.
  const hintClass = (i, n) => (n > 1 && i === 0 ? (fanDown ? ' fab-item--hint-down' : ' fab-item--hint-up') : '');

  return (
    <>
      <div
        className={`fab-wrapper${open ? ' is-open' : ''}${dragging ? ' is-dragging' : ''}${fanRight ? ' fab-wrapper--right' : ''}`}
        ref={wrapperRef}
        style={pos ? { left: pos.x, top: pos.y, right: 'auto', bottom: 'auto' } : undefined}
      >
        <div className="fab-orbit" aria-hidden={!open}>
          {actions.map((a, i) => (
            <button
              key={a.key}
              type="button"
              className={`fab-item${hintClass(i, actions.length)}`}
              style={place(i, actions.length)}
              onClick={a.onClick}
              aria-label={a.label}
              data-hint={a.label}
              tabIndex={open ? 0 : -1}
            >
              <i className={`ph ${a.icon}`} />
            </button>
          ))}
        </div>

        <button
          type="button"
          className="fab-main"
          onClick={() => {
            // The click that ends a drag only drops the button.
            if (justDragged.current) {
              justDragged.current = false;
              return;
            }
            setOpen(!open);
          }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          title="Drag to move"
          aria-expanded={open}
          aria-label={open ? 'Close quick actions' : 'Quick actions'}
        >
          <span className="fab-main__plus">+</span>
        </button>
      </div>

      <Modal
        show={showLocationModal}
        onHide={() => setShowLocationModal(false)}
        centered
        backdrop="static"
        className="select-location-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Select Location</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <label className="form-label">
            Location <span className="text-danger">*</span>
          </label>

          <Select
            options={locationOptions}
            value={location}
            onChange={setLocation}
            isLoading={isLocationLoading}
            placeholder={isLocationLoading ? 'Loading locations...' : 'Select location'}
            styles={{
              menuList: (base) => ({
                ...base,
                maxHeight: 160
              }),
              ...(config.mode === 'dark' ? reactSelectDark : {})
            }}
          />

          <small className="text-muted">Redemption will be recorded under this location.</small>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="light"
            onClick={() => {
              setShowLocationModal(false);
              setLocation(null);
            }}
          >
            Cancel
          </Button>

          <Button variant="primary" disabled={!location} onClick={proceedToScan}>
            Continue
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
